<template>
  <table class="fts-table-view fts-table fts-table--extend">
    <thead>
      <tr>
        <th class="row-checkbox" v-if="bulk">
          <checkbox
            v-model="selectedAll"
            color="var(--fts-primary)"
            :size="16"
            ref="selectedAll"
          />
        </th>
        <th
          v-for="column in getColumns"
          :key="column.id"
          class="table-view-th stopPropagation"
        >
          <div class="table-view-th-col col-header">
            <fts-moji
              :emoji="moduleEmoji(column.connectionId)"
              :size="14"
              class="col-header-emoji"
              v-if="column.connectionId && moduleEmoji(column.connectionId)"
            />
            <i
              class="col-header-icon"
              :class="informationIcon(column.type)"
              v-else-if="informationIcon(column.type)"
            ></i>
            <div class="col-header-name">
              {{ column.label || column.displayName }}
            </div>
          </div>
        </th>
        <th class="table-view-col-footer">
        </th>
      </tr>
      <!-- attachments row -->
      <tr class="tr-sub" v-if="getAttachmentColumns.length">
        <th v-if="bulk"></th>
        <th :colspan="getColumns.length + 1">
          <div class="col-header-tag-container">
            <template v-for="column in getAttachmentColumns">
              <div
                class="col-header col-header-tag"
                :key="column.id"
              >
                <i
                  class="col-header-icon"
                  :class="informationIcon(column.type)"
                  v-if="informationIcon(column.type)"
                ></i>
                <div class="col-header-name">
                  {{ column.label || column.displayName }}
                </div>
              </div>
            </template>
          </div>
        </th>
      </tr>
    </thead>
    <tbody v-for="item in list" :key="item.id">
      <tr
        :key="item.id"
        class="table-view-tr"
        :class="{ 'selected-row': item.selected }"
        @click="selectRow(item)"
      >
        <!-- bulk -->
        <td class="row-checkbox" v-if="bulk">
          <div class="stopPropagation">
            <checkbox
              v-model="item.selected"
              color="var(--fts-primary)"
              :size="16"
            />
          </div>
        </td>
        <!-- columns -->
        <td
          v-for="column in getColumns"
          :key="column.id"
          class="table-view-td"
          :class=" {'table-view-td--primary-col': column.primaryField}"
          :style="column.style"
        >
          <template v-if="column.system">
            <!-- document id -->
            <div
              class="table-view-document-id"
              v-if="column.fieldName === 'documentId'"
            >
              {{ item.documentId }}
            </div>
            <!-- state -->
            <fts-state
              :state="item.state"
              :states="states"
              v-else-if="column.fieldName === 'state'"
            />
            <!-- system date -->
            <div v-else-if="column.fieldName === 'date'">
              {{ moment(item.date).format("ll") }}
            </div>
            <!-- created date -->
            <div v-else-if="column.fieldName === 'createdDate'">
              {{ moment(item.audit.createdDate).format("llll") }}
            </div>
            <!-- created By -->
            <div
              class="fts-audit"
              v-else-if="column.fieldName === 'createdBy'"
            >
              <fts-profile
                :userName="item.audit.createdBy"
                class="audit-profile"
              />
              <fts-tippy
                class="audit-date-wrapper"
                :content="moment(item.audit.createdDate).format('llll')"
              >
                <div slot="trigger" class="audit-date">
                  {{ momentRelativeTime(item.audit.createdDate) }}
                </div>
              </fts-tippy>
            </div>
            <!-- updated date -->
            <div v-else-if="column.fieldName === 'lastChangeDate'">
              {{ moment(item.audit.updatedDate).format("llll") }}
            </div>
            <!-- updated by -->
            <div
              class="fts-audit"
              v-else-if="column.fieldName === 'lastChangeBy'"
            >
              <fts-profile
                :userName="item.audit.updatedBy"
                class="audit-profile"
              />
              <fts-tippy
                class="audit-date-wrapper"
                :content="moment(item.audit.updatedDate).format('llll')"
              >
                <div slot="trigger" class="audit-date">
                  {{ momentRelativeTime(item.audit.updatedDate) }}
                </div>
              </fts-tippy>
            </div>
            <!-- assign to -->
            <fts-assign-to
              :module-id="vectorId"
              :form-data="item"
              :view-mode="!assignRight"
              :comment-section="commentRight"
              class="stopPropagation"
              @refresh="refresh"
              v-else-if="column.fieldName === 'assignTo'"
            />
            <!-- username -->
            <div v-else-if="column.fieldName === 'username'">
              <fts-profile
                :user-name="item.extension.username"
                profile-group
                v-if="item.extension && item.extension.username"
              />
            </div>
          </template>
          <!-- form element -->
          <fts-list-view-display
            :form-data="item.extension || item"
            :item="column"
            :vector-id="vectorId"
            enable-text-alignment
            :comfort-design="comfortDesign"
            v-else
          />
          <!-- SS05909: why this row came back, when the field it matched on is not one of this
               listing's columns. On a column the reader can already see, the row explains itself
               and the hint would be noise. -->
          <div class="search-match-hint" v-if="column.primaryField && hiddenSearchMatch(item)">
            matched on {{ hiddenSearchMatch(item) }}
          </div>
          <template v-if="column.primaryField">
            <!-- shortcut -->
            <div
              class="table-view-shortcut stopPropagation"
              :style="shortcutStyle"
              v-if="(isViewRight || isQuickViewRight)"
            >
              <!-- quick view and view -->
              <fts-btn-hold
                tag="a"
                sm
                text
                :href="entryURL(item.id)"
                text-transform-init
                box-shadow-none
                @clickEvent="executeActivity({ id: 'quickView' }, [item])"
                @longPressEvent="executeActivity({ id: 'view' }, [item], true)"
                v-if="isViewRight"
              >
                <i class="fts-icon-file"></i> View
              </fts-btn-hold>
              <!-- quick view or view -->
              <fts-btn
                tag="a"
                sm
                text
                :href="entryURL(item.id)"
                text-transform-init
                box-shadow-none
                @click.native.prevent="executeActivity({ id: 'quickView' }, [item])"
                v-else
              >
                <i class="fts-icon-file"></i> View
              </fts-btn>
            </div>
            <!-- collaboration -->
            <div
              class="fts-collaboration table-view-collaboration"
              v-if="commentRight"
            >
              <!-- intention -->
              <div
                class="collaboration-wrapper"
                v-if="item.lastIntention && item.lastIntention.id"
              >
                <fts-tippy
                  :options="{ theme: 'light-border' }"
                  :request="{
                    dispatchName: 'loadHistory',
                    payload: {
                      id: item.lastIntention.id,
                      type: 'intention',
                    },
                  }"
                  append-content-class="fts-collaboration-content"
                >
                  <div
                    slot="trigger"
                    class="collaboration-trigger collaboration-intention"
                    :class="{'collaboration-last': (item.collaborationFlags & 8) === 8}"
                  >
                    <i class="fts-icon-exclamation-triangle"></i>
                  </div>
                  <template #content="{ content, loading }">
                    <template v-if="content">
                      <div class="collaboration-intention-content">
                        <div class="collaboration-intention-label">
                          Reported by
                        </div>
                        <fts-profile :userName="collaboration.createdBy" />
                      </div>
                      <div class="collaboration-date">
                        {{ moment(collaboration.createdDate).format("llll") }}
                      </div>
                    </template>
                    <fts-spinner :size="16" show v-else-if="loading" />
                  </template>
                </fts-tippy>
              </div>
              <!-- comment -->
              <div
                class="collaboration-wrapper"
                v-if="item.lastComment && item.lastComment.id"
              >
                <fts-tippy
                  :options="{ theme: 'light-border' }"
                  :request="{
                    dispatchName: 'loadHistory',
                    payload: {
                      id: item.lastComment.id,
                      type: item.lastComment.type
                        ? item.lastComment.type
                        : 'comment',
                    },
                  }"
                  append-content-class="fts-collaboration-content"
                >
                  <div
                    slot="trigger"
                    class="collaboration-trigger collaboration-comment"
                    :class="{'collaboration-last': (item.collaborationFlags & 4) === 4}"
                  >
                    <i class="fts-icon-comment"></i>
                  </div>
                  <template #content="{ content, loading }">
                    <template v-if="content">
                      <div class="collaboration-comment-audit">
                        <fts-profile :userName="content.createdBy" />
                        <div class="comment-from-now">
                          {{ momentRelativeTime(content.createdDate) }}
                        </div>
                      </div>
                      <div
                        class="collaboration-comment-content"
                        v-html="translateCommentContent(content.detail)"
                      ></div>
                      <div class="collaboration-date">
                        {{ moment(content.createdDate).format("llll") }}
                      </div>
                    </template>
                    <fts-spinner :size="16" show v-else-if="loading" />
                  </template>
                </fts-tippy>
                <div class="collaboration-count" v-if="item.commentCount">
                  {{ item.commentCount > 99 ? 99 : item.commentCount }}
                </div>
              </div>
            </div>
            <!-- standard menu -->
            <fts-standard-menu
              :item="item"
              :standard-actions="standardActions"
              :read-only="readOnlySource"
              :module-id="vectorId"
              :list-id="listId"
              @executed="
                executeActivity(
                  $event,
                  list.filter((x) => x.selected)
                )
              "
              class="table-view-standard stopPropagation"
              v-if="!bulk && item.selected"
            />
          </template>
        </td>
        <!-- column:footer -->
        <td class="table-view-col-footer"></td>
      </tr>
      <!-- attachments row -->
      <tr
        :key="`tr-extended-${item.id}`"
        :class="{ 'selected-row': item.selected }"
        @click="selectRow(item)"
        v-if="getAttachmentColumns.length"
      >
        <!-- bulk -->
        <td class="sub-row" v-if="bulk"></td>
        <!-- column -->
        <td class="sub-row" :colspan="getColumns.length">
          <div class="col-file-container">
            <template v-for="attachmentColumn in getAttachmentColumns">
              <template
                v-if="
                  getAttachmentModel(
                    item.extension || item,
                    attachmentColumn.fieldName
                  )
                "
              >
                <fts-file
                  v-for="file in getAttachmentModel(
                    item.extension || item,
                    attachmentColumn.fieldName
                  )"
                  :key="file.key"
                  :prefixURL="`${vectorId}-${attachmentColumn.id}`"
                  :data="file"
                  fileType="file"
                  downloadable
                  class="col-file-wrapper"
                />
              </template>
            </template>
          </div>
        </td>
        <!-- column:footer -->
        <td class="sub-row table-view-col-footer"></td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import moment from 'moment';
import commons from '../../commons/commons';
import { hiddenSearchMatch, shownColumns } from '../../commons/searchMatchHint';
import { clientStore } from '../../store/clientStore';

import Checkbox from 'vue-material-checkbox';

import FTSAssignTo from '../FTSAssignTo';
import FTSBtn from '../FTSBtn';
import FTSBtnHold from '../FTSBtnHold';
import FTSFile from '../file/FTSFile';
import FTSMoji from '../FTSMoji';
import FTSProfile from '../FTSProfile';
import FTSSpinner from '../FTSSpinner';
import FTSStandardMenu from '../FTSStandardMenu';
import FTSState from '../FTSState';
import FTSTippy from '../FTSTippy';

export default {
  props: {
    bulk: Boolean,
    collaboration: Boolean,
    attachmentsRow: Boolean,
    editable: Boolean,
    vectorId: [String, Number],
    listId: String,
    states: Array,
    users: Array,
    columns: Array,
    list: Array,
    standardActions: Array,
    /* SS06274: header.readOnlySource of the listing, handed on to the row menu */
    readOnlySource: Boolean,
    comfortDesign: Boolean,
    moduleInformation: Array,
    information: Array,
    /* SS05909: what the reader searched for, so a row can say which field it matched on */
    searchTerm: String,
  },
  components: {
    checkbox: Checkbox,
    "fts-assign-to": FTSAssignTo,
    "fts-btn": FTSBtn,
    "fts-btn-hold": FTSBtnHold,
    "fts-file": FTSFile,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-spinner": FTSSpinner,
    "fts-standard-menu": FTSStandardMenu,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    informationIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    moduleEmoji(connectionId) {
      const res = this.workspaceModules.find(x => x.id == connectionId);
      return res && res.emoji;
    },
    entryURL(id) {
      return commons.clientPathUrl({
        moduleId: this.vectorId,
        listId: this.listId,
        id: id,
      });
    },
    selectRow(item) {
      if (!this.editable || commons.elementStopPropagation())
        return;

      const value = !Boolean(item && item.selected);
      if (!this.bulk) {
        this.list.forEach((x) => {
          this.$set(x, "selected", false);
        });
      }
      this.$set(item, "selected", value);     
    },
    executeActivity(activity, items, fullView) {
      if (activity.id === 'view' && !this.isViewRight) {
        activity.id = 'quickView';
      } else if (activity.id === 'quickView' && !this.isQuickViewRight) {
        activity.id = 'view';
        fullView = true;
      }
      this.$emit("activityExecuted", {
        activity: activity,
        items: items,
        fullView: fullView,
      });
    },
    getAttachmentModel(formData, columnFieldName) {
      const result = commons.model(formData, columnFieldName);
      if (result) return Array.isArray(result) ? result : [result];
    },
    refresh() {
      this.$emit("refresh");
    },
    translateCommentContent(content) {
      if (!!content)
        return commons.translateCommentContent(content, this.users);
    },
    /* SS05909: the field a searched row matched on, named only when this table does not already
       show it. The rule lives in commons/searchMatchHint, shared with the Lite listing. */
    hiddenSearchMatch(item) {
      return hiddenSearchMatch(item, shownColumns(this.columns), this.searchTerm);
    }
  },
  computed: {
    isQuickViewRight() {
      return this.standardActions && this.standardActions.indexOf("quickView") > -1;
    },
    isViewRight() {
      return this.standardActions && this.standardActions.indexOf("view") > -1;
    },
    assignRight() {
      return this.standardActions && this.standardActions.indexOf("assign") > -1;
    },
    commentRight() {
      return this.standardActions && this.standardActions.indexOf("comment") > -1;
    },
    getColumns() {
      let columns = _.cloneDeep(this.columns);
      /* exclude attachments */
      if (this.attachmentsRow)
        columns = columns.filter(
          (col) => col.type !== 12 && col.type !== 13
        );
      /* exclude hidden */
      columns = columns.filter(
        (col) =>
          (col.show === null || col.show === undefined || col.show === true) &&
          (col.hide === null || col.hide === undefined || col.hide !== true)
      );
      /* set primary field */
      columns && columns[0] && (columns[0].primaryField = true);
      /* iter */
      columns.forEach(x => {
        let width = null;
        /* calculate width */
        if (x.width) {
          width = x.width < 100 ? 100 : x.width;
        }
        /* primary field */
        if (x.primaryField) {
          let paddingRight = 16; /* 1rem */
          if (this.commentRight) {
            paddingRight += 48; /* 3rem */
          }
          if (this.isViewRight || this.isQuickViewRight) {
            paddingRight += 76; /* 4.75rem */
          }
          x.style = { 'padding-right': `${paddingRight}px`};
          if (width) {
            width += paddingRight;
          }
        }
        /* set width */
        if (width) {
          x.style = {
            ...x.style,
            'max-width': `${width}px`,
            'min-width': `${width}px`,
            'width': `${width}px`,
          };
        }
      });
      return columns;
    },
    getAttachmentColumns() {
      if (this.attachmentsRow)
        return this.columns.filter(
          (col) =>
            (col.type === 12 || col.type === 13) &&
            (col.show === null ||
              col.show === undefined ||
              col.show === true) &&
            (col.hide === null || col.hide === undefined || col.hide !== true)
        );
      return [];
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    selectedAll: {
      get() {
        return this.list.every((x) => x.selected);
      },
      set(val) {
        this.list.forEach((x) => {
          this.$set(x, "selected", val);
        });
      },
    },
    selection() {
      return this.list.filter((x) => x.selected);
    },
    shortcutStyle() {
      return { 'right': this.commentRight ? '4.125rem' : '1.375rem'};
    }
  },
  watch: {
    selection() {
      this.$emit("selectionChanged", this.selection);
    },
  },
  beforeCreate: function () {
    this.$options.components.FtsListViewDisplay =
      require("../FTSListViewDisplay").default;
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-table-view {
  .table-view-document-id {
    font-weight: 500;
  }
  /* SS05909: why a searched row came back, when the field it matched on is not a column here.
     Quiet by design - it explains a row, it does not compete with its content. */
  .search-match-hint {
    margin-top: 2px;
    font-size: var(--inistate-label-text-12, 12px);
    line-height: var(--inistate-label-text-12--line-height, 16px);
    color: var(--inistate-text-secondary, #878787);
  }
  .table-view-shortcut {
    @include user-select-none;
    display: none;
    position: absolute;
    top: 0.25rem;
    box-shadow: var(--fts-elevation-2);
    border-radius: 0.25rem;
    background: var(--fts-popover-alt-background);
    > .fts-btn {
      padding: 0.25rem 0.375rem;
      min-height: 0;
      height: auto;
      i {
        margin-right: 0.25rem;
      }
    }
  }
  .table-view-collaboration {
    position: absolute;
    top: 0.375rem;
    right: 1.375rem;
  }
  .table-view-standard {
    position: absolute;
    top: 0.375rem;
    right: 0;
  }
  .table-view-col-footer {
    width: 100%;
    padding: 0 1rem;
    &:first-child {
      padding-left: 0.125rem;
    }
  }
  .table-view-control {
    display: flex;
  }
  .table-view-th {
    padding: 0;
  }
  .table-view-th-col {
    padding: 0.375rem 0.75rem;
  }
  .table-view-td {
    white-space: nowrap;
    &.table-view-td--primary-col {
      position: relative;
    }
  }
  tbody:hover {
    .table-view-shortcut {
      display: block;
    }
  }
  /* override */
  .row-checkbox {
    width: 1px;
    .m-chckbox--container {
      margin: 0;
      vertical-align: text-top;
      .m-chckbox--label {
        display: none;
      }
    }
  }
}
</style>